// lib/koi-pond-v3/particles/fireflies.ts
//
// T10.1 — fireflies. A handful of warm amber point-glows drifting above
// the water on slow Brownian paths, each blinking on its own cycle
// (fade in ~0.5s → hold 1–3s → fade out ~0.8s → dark 2–5s). Each casts a
// faint warm reflection on the water directly below, stronger when it
// drifts near the surface.
//
// Each firefly is an additive billboard (core + soft halo) screen-aligned
// each frame; the reflection is a flat additive quad on the water plane.
// They live on layer 0 so the DoF pass turns foreground fireflies into
// soft bokeh — which is exactly the look we want.

import {
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Group,
  Color,
  AdditiveBlending,
  type Camera,
} from 'three';
import { palette } from '../palette';
import { scatterDecay } from '../interaction/dragonGatePhases';

const GLOW_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// shared glow fragment: tight core + soft halo, scaled by uIntensity
const GLOW_FRAG = /* glsl */ `
  precision highp float;
  uniform vec3 uCore;
  uniform vec3 uHalo;
  uniform float uIntensity;
  varying vec2 vUv;
  void main() {
    vec2 d = vUv - 0.5;
    float r = length(d) * 2.0;
    float core = exp(-r * r * 9.0);
    float halo = exp(-r * r * 2.2) * 0.5;
    float a = (core + halo) * uIntensity;
    vec3 col = mix(uHalo, uCore, core);
    gl_FragColor = vec4(col, a); // additive (SrcAlpha, One)
  }
`;

function makeGlowMaterial(coreHex: string, haloHex: string) {
  return new ShaderMaterial({
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    uniforms: {
      uCore: { value: new Color(coreHex) },
      uHalo: { value: new Color(haloHex) },
      uIntensity: { value: 0 },
    },
    vertexShader: GLOW_VERT,
    fragmentShader: GLOW_FRAG,
  });
}

const hash = (n: number) => {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
};

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// blink cycle in [0,1] of a per-firefly period: fade in / hold / fade out / dark
function blink(t: number, seed: number): number {
  const period = 6 + hash(seed) * 3.5; // 6–9.5s
  const ph = (t / period + hash(seed + 9)) % 1;
  if (ph < 0.07) return smoothstep(0, 0.07, ph); // ~0.5s fade in
  if (ph < 0.4) return 1; // ~2s hold
  if (ph < 0.52) return 1 - smoothstep(0.4, 0.52, ph); // ~0.8s fade out
  return 0; // dark
}

interface Firefly {
  glow: Mesh;
  refl: Mesh;
  glowMat: ShaderMaterial;
  reflMat: ShaderMaterial;
  base: [number, number, number];
  seed: number;
  // dragon-gate scatter impulse (rest: scatterStart=-1 → no offset)
  scatterDir: [number, number];
  scatterStart: number;
  scatterStrength: number;
}

export function makeFireflies(count = 5) {
  const group = new Group();
  const flies: Firefly[] = [];

  for (let i = 0; i < count; i++) {
    // own material per firefly so each blinks on its own cycle
    const glowMat = makeGlowMaterial(palette.fireflyAmber.hex, palette.fireflyGlow.hex);
    const reflMat = makeGlowMaterial(palette.fireflyGlow.hex, palette.fireflyGlow.hex);
    const glow = new Mesh(new PlaneGeometry(0.32, 0.32), glowMat);
    glow.renderOrder = 4;
    const refl = new Mesh(new PlaneGeometry(0.5, 0.5), reflMat);
    refl.rotation.x = -Math.PI / 2; // lie flat on the water
    refl.renderOrder = 3;
    group.add(glow);
    group.add(refl);
    // scattered across the foreground/midground above the water (y=1)
    const base: [number, number, number] = [
      -2.2 + hash(i + 1) * 5.0,
      1.35 + hash(i + 3) * 0.85,
      0.2 + hash(i + 5) * 3.6,
    ];
    flies.push({ glow, refl, glowMat, reflMat, base, seed: i * 4.7 + 1.1, scatterDir: [0, 0], scatterStart: -1, scatterStrength: 0 });
  }

  let now = 0;
  // Dragon Gate — push every firefly outward from (ox,oz) with a decaying
  // offset that eases back over ~2s (a transient impulse over the wander).
  function scatter(ox: number, oz: number, strength: number) {
    for (const f of flies) {
      const dx = f.base[0] - ox, dz = f.base[2] - oz;
      const len = Math.hypot(dx, dz) || 1;
      f.scatterDir = [dx / len, dz / len];
      f.scatterStart = now;
      f.scatterStrength = strength;
    }
  }

  function update(t: number, camera: Camera) {
    now = t;
    for (const f of flies) {
      const b = blink(t, f.seed);
      // slow Brownian-ish wander (layered sines per axis)
      const wx = 0.4 * Math.sin(t * 0.21 + f.seed) + 0.18 * Math.sin(t * 0.53 + f.seed * 2.1);
      const wy = 0.22 * Math.sin(t * 0.17 + f.seed * 1.7);
      const wz = 0.4 * Math.cos(t * 0.19 + f.seed * 1.3) + 0.18 * Math.sin(t * 0.47 + f.seed);
      const sAge = f.scatterStart < 0 ? -1 : t - f.scatterStart;
      const sMag = scatterDecay(sAge) * f.scatterStrength;
      const x = f.base[0] + wx + f.scatterDir[0] * sMag;
      const y = f.base[1] + wy;
      const z = f.base[2] + wz + f.scatterDir[1] * sMag;

      f.glow.position.set(x, y, z);
      f.glow.quaternion.copy(camera.quaternion); // screen-aligned billboard
      f.glowMat.uniforms.uIntensity.value = b;

      // faint warm reflection on the water below — stronger near the surface
      const surfaceProx = Math.max(0, 1 - (y - 1) / 1.4);
      f.refl.position.set(x, 1.02, z);
      f.reflMat.uniforms.uIntensity.value = b * surfaceProx * 0.35;
    }
  }

  function dispose() {
    for (const f of flies) {
      f.glowMat.dispose();
      f.reflMat.dispose();
      f.glow.geometry.dispose();
      f.refl.geometry.dispose();
    }
  }

  return { object: group, update, scatter, dispose };
}
